import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { AdCreative, detectDevice, serveAds } from '../../api/ads/ads.api';
import { PRE_ROLL } from '../../api/ads/ad-placements';
import { getHiddenIds } from './ad-tracking';
import { claimPreRollSlot, markPreRollMissed } from './pre-roll-frequency';
import { useViewerProStatus } from './use-viewer-pro';

/** Chờ máy chủ quảng cáo tối đa chừng này lúc người xem bấm xem; quá hạn thì video chính chạy ngay, bỏ câu trả lời đến muộn. */
export const PRE_ROLL_SERVE_TIMEOUT_MS = 1500;

/**
 * Hỏi một quảng cáo chèn trước cho bài video này. Không có, quá hạn, lỗi (kể cả khách bị 401), quảng cáo thiếu video https
 * hoặc người xem đã ẩn quảng cáo đó: trả null.
 */
export const fetchPreRollAd = (postId: string): Promise<AdCreative | null> =>
  new Promise((resolve) => {
    let done = false;
    const settle = (ad: AdCreative | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(ad);
    };
    const timer = setTimeout(() => settle(null), PRE_ROLL_SERVE_TIMEOUT_MS);
    serveAds({ placement: PRE_ROLL, postId, count: 1, device: detectDevice() })
      .then((res) => {
        const ad = res?.[0];
        const ok = !!ad && /^https:\/\//i.test(ad.videoUrl || '') && !getHiddenIds().includes(ad.campaignId);
        settle(ok ? ad : null);
      })
      .catch(() => settle(null));
  });

export const sameUser = (a?: string | null, b?: string | null) =>
  !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

/**
 * Quyết định có hỏi quảng cáo chèn trước khi người xem bấm xem một video không. `begin()` gọi đúng lúc bấm xem (không gọi lúc
 * vẽ): trả null ngay nếu lần này không hỏi (video của chính người xem, người xem có Cab+ Pro, chưa tới lượt theo tần suất),
 * khi đó video chính cứ chạy; còn lại trả Promise quảng cáo hoặc null. Khách chưa đăng nhập vẫn hỏi, máy chủ quyết định.
 */
export const usePreRoll = ({ postId, ownerId }: { postId?: string | null; ownerId?: string | null }) => {
  const viewerId = useSelector((s: RootState) => s.features.auth.profile?.userId);
  const { isPro } = useViewerProStatus();
  const own = sameUser(viewerId, ownerId);

  const begin = useCallback((): Promise<AdCreative | null> | null => {
    if (!postId || own || isPro) return null;
    if (!claimPreRollSlot()) return null;
    return fetchPreRollAd(postId).then((ad) => {
      if (!ad) markPreRollMissed();
      return ad;
    });
  }, [postId, own, isPro]);

  return { begin };
};
